import { useEffect, useState, useCallback, useRef } from "react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search, Plus, BadgeCheck, PhoneCall, MessageCircle, ChevronLeft, ChevronRight, UserCog, Check, X, FileText, Download, Upload, Loader2 } from "lucide-react";
import { Progress } from "../components/ui/progress";
import { format, subDays, startOfMonth } from "date-fns";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

// Matches routes_ingest.STAGES — duplicated locally rather than imported,
// same self-contained-constants convention every other page in this app
// already follows (ApplicationTracker.jsx has its own copy too).
const WALKIN_STAGE_LABELS = {
  lead: "Lead Captured", application_initiated: "Application Initiated",
  doc_verified: "Document Verified", offer_issued: "Offer Issued",
  fee_paid: "Fee Paid", enrolled: "Enrolled",
};
const WALKIN_STAGE_ORDER = Object.keys(WALKIN_STAGE_LABELS);

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh",
  "Lakshadweep", "Puducherry",
];

const iso = (d) => format(d, "yyyy-MM-dd");
const DATE_PRESETS = [
  { v: "all", label: "Any date" },
  { v: "today", label: "Today" },
  { v: "7d", label: "Last 7 days" },
  { v: "month", label: "This month" },
  { v: "custom", label: "Custom range" },
];
function presetRange(v) {
  const now = new Date();
  if (v === "today") return { from: iso(now), to: iso(now) };
  if (v === "7d") return { from: iso(subDays(now, 6)), to: iso(now) };
  if (v === "month") return { from: iso(startOfMonth(now)), to: iso(now) };
  return { from: "", to: "" };
}

const TIER_STYLE = {
  primary: "bg-blue-100 text-blue-800",
  secondary: "bg-indigo-100 text-indigo-800",
  tertiary: "bg-purple-100 text-purple-800",
};

const DOC_BADGE = {
  pending_verification: "bg-amber-100 text-amber-800",
  verified: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

const ADMIN_ROLES = ["super_admin", "dean"];
const FILES_ORIGIN = API.replace(/\/api\/?$/, "");

function CounselorBadge({ name }) {
  if (!name) return <Badge className="bg-slate-100 text-slate-500">Unassigned</Badge>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-6 h-6 shrink-0 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">
        {name.charAt(0)}
      </span>
      <span className="text-sm text-slate-700">{name}</span>
    </span>
  );
}

export default function Leads() {
  const { viewRole, user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(viewRole);

  const [data, setData] = useState({ rows: [], total: 0 });
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("");
  const [verification, setVerification] = useState("");
  const [source, setSource] = useState("");
  const [counselorFilter, setCounselorFilter] = useState("");
  const [dates, setDates] = useState({ from: "", to: "" });
  const [datePreset, setDatePreset] = useState("all");
  const [sources, setSources] = useState([]);
  const [courses, setCourses] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [callLead, setCallLead] = useState(null);
  const [exporting, setExporting] = useState(false);
  const pageSize = 12;

  const fetchLeads = useCallback(() => {
    api.get("/leads", { params: {
      search, tier, verification, source, assigned: counselorFilter,
      from_date: dates.from || undefined, to_date: dates.to || undefined,
      page, page_size: pageSize,
    } }).then((r) => setData(r.data)).catch(() => {});
  }, [search, tier, verification, source, counselorFilter, dates.from, dates.to, page]);

  const setDate = (k) => (val) => { setDatePreset("custom"); setPage(1); setDates((d) => ({ ...d, [k]: val })); };
  const applyDatePreset = (v) => { setDatePreset(v); setPage(1); if (v !== "custom") setDates(presetRange(v)); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {
        search, tier, verification, source, assigned: counselorFilter,
        from_date: dates.from || undefined, to_date: dates.to || undefined,
      };
      const r = await api.get("/leads/export", { params, responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : "leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  const loadSources = useCallback(() => {
    // Same institution-wide filter-options endpoint every other filter bar
    // in the app already reads from (Applications.jsx, CampaignUsage.jsx)
    // — courses live in db.courses (routes_institution.py's Programs &
    // Courses catalog), not gated to admin roles here since any counselor
    // capturing a walk-in needs this same list.
    api.get("/analytics/filter-options")
      .then((r) => { setSources(r.data.sources || []); setCourses(r.data.courses || []); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadSources(); }, [loadSources]);
  useEffect(() => {
    if (!isAdmin) { setCounselors([]); return; }
    api.get("/admin/assignment/counselors").then((r) => setCounselors(r.data)).catch(() => {});
  }, [isAdmin]);

  const openLead = async (id) => {
    const r = await api.get(`/leads/${id}`);
    setSelectedLead(r.data);
  };

  const verify = async (id) => {
    await api.post(`/leads/${id}/verify`);
    toast.success("Lead marked OTP verified");
    openLead(id);
    fetchLeads();
  };

  const setDocStatus = async (docId, status) => {
    try {
      await api.patch(`/documents/${docId}`, { status });
      toast.success(`Document ${status.replace(/_/g, " ")}`);
      if (selectedLead) openLead(selectedLead.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reassign = async (leadIds, payload) => {
    const ids = Array.isArray(leadIds) ? leadIds : [leadIds];
    const results = await Promise.allSettled(ids.map((id) => api.post(`/admin/leads/${id}/assign`, payload)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok) toast.success(`Reassigned ${ok} lead${ok === 1 ? "" : "s"}${fail ? `, ${fail} failed` : ""}`);
    else toast.error(formatApiError(results[0]?.reason?.response?.data?.detail) || "Reassignment failed");
    setSelected(new Set());
    fetchLeads();
  };

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const pageIds = data.rows.map((r) => r.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pageIds));
  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const resetSelection = () => setSelected(new Set());
  const colCount = 9 + (isAdmin ? 1 : 0);

  return (
    <div data-testid="leads-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Leads Engine</h1>
          <p className="text-sm text-slate-500 mt-1">Deduplicated lead records with multi-instance attribution.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {isAdmin && (
            <Button variant="outline" data-testid="leads-export-csv" className="flex-1 sm:flex-none"
              onClick={exportCsv} disabled={exporting}>
              <Download className="w-4 h-4 mr-1.5" /> {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          )}
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="leads-import-button" className="flex-1 sm:flex-none">
                <Upload className="w-4 h-4 mr-1.5" /> Import Leads
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Bulk import leads</DialogTitle></DialogHeader>
              <ImportForm
                onDone={() => { setImportOpen(false); fetchLeads(); loadSources(); }}
              />
            </DialogContent>
          </Dialog>
          <Dialog open={walkinOpen} onOpenChange={setWalkinOpen}>
            <DialogTrigger asChild>
              <Button data-testid="walkin-lead-button" className="bg-brand flex-1 sm:flex-none">
                <Plus className="w-4 h-4 mr-1.5" /> Walk-in Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl">
              <DialogHeader><DialogTitle>Capture Walk-in Lead</DialogTitle></DialogHeader>
              <WalkInForm
                courses={courses} counselors={counselors} isAdmin={isAdmin} currentUser={user}
                onDone={() => { setWalkinOpen(false); fetchLeads(); }}
                onOpenLead={(id) => { setWalkinOpen(false); openLead(id); }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-4 border-slate-200 flex flex-wrap gap-2" data-testid="leads-filter-bar">
        <div className="relative w-full sm:w-auto">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="leads-search-input" placeholder="Name, email or phone…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 h-9 w-full sm:w-56 text-sm bg-white" />
        </div>
        {[
          { val: tier, set: setTier, opts: ["primary", "secondary", "tertiary"], ph: "Attribution Tier", id: "leads-filter-tier" },
          { val: verification, set: setVerification, opts: ["unverified", "otp_verified"], ph: "Verification", id: "leads-filter-verification" },
          { val: source, set: setSource, opts: sources, ph: "Source", id: "leads-filter-source" },
        ].map((f) => (
          <Select key={f.id} value={f.val || "__all__"} onValueChange={(v) => { f.set(v === "__all__" ? "" : v); setPage(1); }}>
            <SelectTrigger data-testid={f.id} className="w-[160px] h-9 text-xs bg-white"><SelectValue placeholder={f.ph} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{f.ph}: All</SelectItem>
              {f.opts.map((o) => <SelectItem key={o} value={o}>{o.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        {isAdmin && (
          <Select value={counselorFilter || "__all__"}
            onValueChange={(v) => { setCounselorFilter(v === "__all__" ? "" : v); setPage(1); resetSelection(); }}>
            <SelectTrigger data-testid="leads-filter-counselor" className="w-[180px] h-9 text-xs bg-white">
              <SelectValue placeholder="Counselor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Counselor: All</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {counselors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Select value={datePreset} onValueChange={applyDatePreset}>
          <SelectTrigger data-testid="leads-filter-date-preset" className="w-[140px] h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DATE_PRESETS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <input type="date" data-testid="leads-from-date" value={dates.from} max={dates.to || undefined}
            onChange={(e) => setDate("from")(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700" aria-label="From date" />
          <span className="text-xs text-slate-400">–</span>
          <input type="date" data-testid="leads-to-date" value={dates.to} min={dates.from || undefined}
            onChange={(e) => setDate("to")(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700" aria-label="To date" />
          {(dates.from || dates.to) && (
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Clear dates"
              data-testid="leads-clear-dates"
              onClick={() => { setDates({ from: "", to: "" }); setDatePreset("all"); setPage(1); }}>
              <X className="w-3.5 h-3.5 text-slate-400" />
            </Button>
          )}
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden" data-testid="leads-table-card">
        {isAdmin && selected.size > 0 && (
          <div className="p-3 border-b border-slate-200 bg-blue-50/50 flex flex-wrap items-center gap-3" data-testid="leads-bulk-bar">
            <span className="text-sm font-medium text-slate-700">{selected.size} selected</span>
            <Select value={bulkTarget} onValueChange={setBulkTarget}>
              <SelectTrigger data-testid="bulk-reassign-select" className="w-[200px] h-9 text-sm bg-white">
                <SelectValue placeholder="Reassign to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto-assign</SelectItem>
                {counselors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" data-testid="bulk-reassign-apply" disabled={!bulkTarget}
              className="bg-brand"
              onClick={() => { reassign([...selected], bulkTarget === "__auto__" ? { auto: true } : { counselor_id: bulkTarget }); setBulkTarget(""); }}>
              Apply
            </Button>
            <Button size="sm" variant="ghost" onClick={resetSelection}>Clear</Button>
          </div>
        )}
        <div className="w-full overflow-auto max-h-[calc(100vh-300px)]" data-testid="leads-table-scroll">
          <table className="w-full text-sm min-w-[860px] border-collapse
            [&_th]:px-3 [&_th]:h-10 [&_th]:whitespace-nowrap [&_th]:sticky [&_th]:top-0 [&_th]:z-10
            [&_th]:bg-slate-50 [&_th]:shadow-[inset_0_-1px_0_#e2e8f0]
            [&_td]:px-3 [&_td]:py-2.5 [&_td]:align-middle [&_tr]:border-b [&_tr]:border-slate-100">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {isAdmin && (
                  <TableHead className="w-8 text-left">
                    <Checkbox data-testid="leads-select-all" checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                  </TableHead>
                )}
                {["Candidate", "Contact", "Location", "Verification", "Attribution", "Instances", "Sources", "Counselor"].map((h) => (
                  <TableHead key={h} className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
                <TableHead className="w-[104px] text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={colCount} className="text-center py-10 text-slate-400" data-testid="leads-empty-state">No leads found.</TableCell></TableRow>
              )}
              {data.rows.map((l, i) => (
                <TableRow key={l.id} data-testid={`leads-table-row-${i}`} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => openLead(l.id)}>
                  {isAdmin && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox data-testid={`lead-select-${i}`} checked={selected.has(l.id)} onCheckedChange={() => toggleOne(l.id)} aria-label="Select lead" />
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap">
                    <div className="font-medium text-slate-800">{l.name}</div>
                    <div className="text-[11px] text-slate-400 font-mono" title={l.id}>
                      {l.id && l.id.length > 12 ? `${l.id.slice(0, 8)}…` : l.id}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="text-xs text-slate-600 max-w-[168px] truncate" title={l.email}>{l.email}</div>
                    <div className="text-xs text-slate-500 font-mono">{l.phone}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap max-w-[132px] truncate"
                    title={`${l.city || ""}${l.city && l.state ? ", " : ""}${l.state || ""}`}>
                    {l.city}{l.city && l.state ? ", " : ""}{l.state}
                  </TableCell>
                  <TableCell>
                    <Badge className={l.verification_status === "unverified" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>
                      {l.verification_status === "unverified" ? "Unverified" : "Verified"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {l.max_tier !== "-" ? (
                      <Badge className={TIER_STYLE[l.max_tier]}>{l.max_tier}</Badge>
                    ) : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="font-mono text-center">{l.instance_count}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[130px] truncate" title={(l.sources || []).join(", ")}>{(l.sources || []).join(", ")}</TableCell>
                  <TableCell className="whitespace-nowrap" data-testid={`lead-counselor-${i}`}><CounselorBadge name={l.assigned_counselor_name} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-0.5 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`lead-call-${i}`} aria-label="Call"
                        onClick={() => { if (l.phone) window.location.href = `tel:${l.phone}`; setCallLead(l); }}>
                        <PhoneCall className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`lead-whatsapp-${i}`} aria-label="WhatsApp"
                        onClick={() => l.phone && window.open(`https://wa.me/91${l.phone}`, "_blank")}>
                        <MessageCircle className="w-4 h-4 text-emerald-600" />
                      </Button>
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`lead-reassign-${i}`} aria-label="Reassign counselor">
                              <UserCog className="w-4 h-4 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuLabel>Assign to</DropdownMenuLabel>
                            <DropdownMenuItem data-testid={`lead-reassign-auto-${i}`} onClick={() => reassign(l.id, { auto: true })}>
                              Auto-assign (use strategy)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {counselors.map((c) => (
                              <DropdownMenuItem key={c.id} onClick={() => reassign(l.id, { counselor_id: c.id })}>
                                {c.name}
                                {l.assigned_counselor_id === c.id && <Check className="w-3.5 h-3.5 ml-auto text-slate-500" />}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </table>
        </div>
        <div className="p-4 flex items-center justify-between border-t border-slate-200">
          <span className="text-xs text-slate-500" data-testid="leads-pagination-info">Page {page} of {totalPages} · {data.total} leads</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="leads-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" data-testid="leads-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <Sheet open={!!selectedLead} onOpenChange={(o) => !o && setSelectedLead(null)}>
        <SheetContent className="w-full sm:w-[480px] sm:max-w-[480px] overflow-y-auto" data-testid="lead-detail-drawer">
          {selectedLead && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display">{selectedLead.name}</SheetTitle>
                <div className="flex flex-wrap gap-2 mt-1">
                  <Badge className={selectedLead.verification_status === "unverified" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>
                    {selectedLead.verification_status.replace("_", " ")}
                  </Badge>
                  <Badge className={TIER_STYLE[selectedLead.attribution_tier]}>{selectedLead.attribution_tier} attribution</Badge>
                  {selectedLead.assigned_counselor_name && (
                    <Badge className="bg-blue-100 text-blue-800">Counselor: {selectedLead.assigned_counselor_name}</Badge>
                  )}
                </div>
              </SheetHeader>
              <Tabs defaultValue="overview" className="mt-6">
                <TabsList>
                  <TabsTrigger value="overview" data-testid="lead-tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="instances" data-testid="lead-tab-instances">Attribution Flow</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[["Email", selectedLead.email], ["Phone", selectedLead.phone], ["City", selectedLead.city],
                      ["State", selectedLead.state], ["Country", selectedLead.country], ["PIN", selectedLead.pin_code]].map(([k, v]) => (
                      <div key={k}>
                        <div className="text-xs uppercase tracking-wider text-slate-400">{k}</div>
                        <div className="text-slate-800">{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Custom Attributes</div>
                    {Object.keys(selectedLead.custom_attributes || {}).length === 0 ? (
                      <div className="text-sm text-slate-400">None captured</div>
                    ) : (
                      <div className="bg-slate-50 rounded-lg p-3 font-mono text-xs space-y-1">
                        {Object.entries(selectedLead.custom_attributes).map(([k, v]) => (
                          <div key={k} className="flex justify-between"><span className="text-slate-500">{k}</span><span className="text-slate-800">{String(v)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(selectedLead.documents || []).length > 0 && (
                    <div data-testid="lead-documents">
                      <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Documents</div>
                      <div className="space-y-2">
                        {selectedLead.documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg p-2">
                            <a href={`${FILES_ORIGIN}/api/documents/${doc.id}`} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1.5 text-xs text-slate-700 truncate underline">
                              <FileText className="w-3.5 h-3.5 shrink-0" />{doc.label}
                            </a>
                            <div className="flex items-center gap-1 shrink-0">
                              <Badge className={DOC_BADGE[doc.status] || "bg-slate-100 text-slate-600"}>{doc.status.replace(/_/g, " ")}</Badge>
                              {isAdmin && doc.status !== "verified" && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Verify document"
                                  data-testid={`doc-verify-${doc.doc_type}`} onClick={() => setDocStatus(doc.id, "verified")}>
                                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                                </Button>
                              )}
                              {isAdmin && doc.status !== "rejected" && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Reject document"
                                  data-testid={`doc-reject-${doc.doc_type}`} onClick={() => setDocStatus(doc.id, "rejected")}>
                                  <X className="w-3.5 h-3.5 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedLead.verification_status === "unverified" && (
                    <Button data-testid="lead-verify-button" onClick={() => verify(selectedLead.id)} className="w-full bg-emerald-600 hover:bg-emerald-700">
                      <BadgeCheck className="w-4 h-4 mr-1.5" /> Mark OTP Verified
                    </Button>
                  )}
                </TabsContent>
                <TabsContent value="instances" className="mt-4 space-y-3">
                  {(selectedLead.instances || []).map((inst, i) => (
                    <div key={inst.id} className="border border-slate-200 rounded-lg p-3" data-testid={`lead-instance-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={TIER_STYLE[inst.tier]}>{inst.tier}</Badge>
                        <span className="text-xs text-slate-400">{inst.created_at?.slice(0, 10)}</span>
                      </div>
                      <div className="text-sm font-medium text-slate-800">{inst.course} {inst.specialization ? `· ${inst.specialization}` : ""}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        via {inst.source} · {inst.campus} · Batch {inst.instance_batch} · Stage: {inst.stage.replace(/_/g, " ")}
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      {callLead && (
        <LogCallDialog lead={callLead} onClose={() => setCallLead(null)}
          onDone={() => { setCallLead(null); fetchLeads(); }} />
      )}
    </div>
  );
}

const CALL_OUTCOMES = [
  { value: "connected", label: "Connected - Interested" },
  { value: "callback_requested", label: "Connected - Callback Requested" },
  { value: "not_interested", label: "Connected - Not Interested" },
  { value: "switched_off", label: "Ringing - No Response" },
  { value: "busy", label: "Busy / Call Dropped" },
  { value: "wrong_number", label: "Invalid / Wrong Number" },
];

const NOT_INTERESTED_REASONS = ["Fees", "Location", "Course Not Available", "Other"];

function toLocalDatetimeInput(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function LogCallDialog({ lead, onClose, onDone }) {
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [followup, setFollowup] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setOutcome(""); setReason(""); setNotes(""); setFollowup("");
  }, [lead?.id]);

  if (!lead) return null;

  const course = lead.instances?.[lead.instances.length - 1]?.course || "";

  const submit = async () => {
    if (!outcome) { toast.error("Pick a call outcome"); return; }
    if (outcome === "callback_requested" && !followup) { toast.error("Pick a next follow-up date/time"); return; }
    setBusy(true);
    try {
      const body = { outcome, notes: notes.trim() || undefined };
      if (outcome === "not_interested" && reason) body.reason = reason;
      if (followup) body.next_followup_at = new Date(followup).toISOString();
      await api.post(`/leads/${lead.id}/log-call`, body);
      const label = CALL_OUTCOMES.find((o) => o.value === outcome)?.label || outcome;
      toast.success(`Call outcome recorded: ${label}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save the call outcome");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="log-call-dialog">
        <DialogHeader>
          <DialogTitle>Log Call Outcome</DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-0.5">
          <div className="font-medium text-slate-800">{lead.name}</div>
          <div className="text-slate-500 font-mono">{lead.phone}</div>
          <div className="text-slate-500">{course || "—"}</div>
          <div className="text-slate-500">Counselor: {lead.assigned_counselor_name || "Unassigned"}</div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Call Outcome / Disposition</Label>
            <Select value={outcome} onValueChange={(v) => {
              setOutcome(v);
              if (v === "callback_requested" && !followup) setFollowup(toLocalDatetimeInput(new Date(Date.now() + 24 * 3600 * 1000)));
              if (v !== "not_interested") setReason("");
            }}>
              <SelectTrigger className="mt-1" data-testid="log-call-outcome"><SelectValue placeholder="Select an outcome" /></SelectTrigger>
              <SelectContent>
                {CALL_OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {outcome === "callback_requested" && (
            <div>
              <Label className="text-xs">Next Follow-Up</Label>
              <input type="datetime-local" value={followup} onChange={(e) => setFollowup(e.target.value)}
                data-testid="log-call-followup"
                className="mt-1 w-full h-9 rounded-md border border-slate-300 px-3 text-sm" />
            </div>
          )}

          {outcome === "not_interested" && (
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={reason || "none"} onValueChange={(v) => setReason(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="log-call-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No reason given</SelectItem>
                  {NOT_INTERESTED_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs">Discussion Notes</Label>
            <Textarea className="mt-1" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="What was discussed / next step" data-testid="log-call-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-brand" onClick={submit} disabled={busy || !outcome} data-testid="log-call-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Update Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WalkInForm({ courses, counselors, isAdmin, currentUser, onDone, onOpenLead }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", state: "", course: "", specialization: "",
    campus: "Main Campus", instance: "2026-27",
    assigned_counselor_id: "round_robin", send_welcome_whatsapp: true,
  });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null); // { leadId, name, stage, counselor } | null
  const [dupChecking, setDupChecking] = useState(false);

  // The course catalog can still be loading (or, for a brand-new tenant
  // with none configured yet, genuinely empty) when this form first opens
  // — default to the first real option the moment the list arrives rather
  // than leaving the dropdown on a blank value.
  useEffect(() => {
    if (courses?.length && !form.course) {
      setForm((f) => ({ ...f, course: courses[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  // Debounced pre-submit duplicate guard: once the phone looks like a real
  // 10-digit number or the email looks valid, check for an existing lead —
  // advisory only (the backend's own ingest_payload dedup is what actually
  // decides primary/secondary/tertiary; this just lets the counselor see
  // it coming and open the existing record instead of guessing).
  useEffect(() => {
    const digits = form.phone.replace(/\D/g, "");
    const phoneValid = digits.length === 10;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!phoneValid && !emailValid) { setDup(null); setDupChecking(false); return; }
    const query = phoneValid ? digits : form.email.trim();
    let alive = true;
    setDupChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/leads", { params: { search: query, page_size: 1 } });
        const hit = data.rows?.[0];
        if (!hit) { if (alive) setDup(null); return; }
        let stage = "Lead Captured";
        try {
          const { data: detail } = await api.get(`/leads/${hit.id}`);
          const apps = detail.applications || [];
          if (apps.length) {
            const rank = (s) => Math.max(0, WALKIN_STAGE_ORDER.indexOf(s));
            const furthest = apps.reduce((best, a) => (rank(a.stage) > rank(best.stage) ? a : best), apps[0]);
            stage = WALKIN_STAGE_LABELS[furthest.stage] || furthest.stage;
          }
        } catch { /* stage is cosmetic — a failed detail fetch shouldn't block the notice */ }
        if (alive) setDup({ leadId: hit.id, name: hit.name, stage, counselor: hit.assigned_counselor_name || "Unassigned" });
      } catch {
        if (alive) setDup(null);
      } finally {
        if (alive) setDupChecking(false);
      }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [form.phone, form.email]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        assigned_counselor_id: form.assigned_counselor_id === "round_robin" ? null : form.assigned_counselor_id,
      };
      const r = await api.post("/leads/walkin", payload);
      toast.success(`Walk-in captured (${r.data.tier} instance)`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = "text") => (
    <div>
      <Label>{label}</Label>
      <Input data-testid={`walkin-${key}`} type={type} value={form[key]} required={["name", "phone"].includes(key)}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="mt-1.5" />
    </div>
  );

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="walkin-form">
      {field("name", "Full Name")}
      {field("phone", "Phone")}
      {field("email", "Email", "email")}
      {/* Full-width, not attached to one specific input — the match can come
          from either the phone or the email field (a repeat visitor might
          give the field they didn't give last time), so it's shown once
          regardless of which one actually found it. */}
      {(dupChecking || dup) && (
        <div className="sm:col-span-2">
          <DuplicateLeadNotice dup={dup} checking={dupChecking} onOpen={onOpenLead} />
        </div>
      )}
      {field("city", "City")}
      <div>
        <Label>State</Label>
        <Input data-testid="walkin-state" list="walkin-state-list" value={form.state}
          onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} className="mt-1.5"
          placeholder="Start typing…" />
        <datalist id="walkin-state-list">
          {INDIAN_STATES.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>
      <div>
        <Label>Course</Label>
        {courses?.length ? (
          <Select value={form.course} onValueChange={(v) => setForm((f) => ({ ...f, course: v }))}>
            <SelectTrigger className="mt-1.5" data-testid="walkin-course"><SelectValue placeholder="Select a course" /></SelectTrigger>
            <SelectContent>
              {courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          // Catalog not loaded yet (or genuinely empty for this tenant) —
          // fall back to free text rather than leaving the form unusable.
          <Input data-testid="walkin-course" value={form.course}
            onChange={(e) => setForm((f) => ({ ...f, course: e.target.value }))} className="mt-1.5" />
        )}
      </div>

      {isAdmin ? (
        <div>
          <Label>Assign Counselor</Label>
          <Select value={form.assigned_counselor_id} onValueChange={(v) => setForm((f) => ({ ...f, assigned_counselor_id: v }))}>
            <SelectTrigger className="mt-1.5" data-testid="walkin-assign-counselor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="round_robin">Round Robin (auto-assign)</SelectItem>
              {(counselors || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div>
          <Label>Counselor</Label>
          <div className="mt-1.5 h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="walkin-self-counselor">
            {currentUser?.name || "You"} <span className="text-slate-400 ml-1.5">(credited to you)</span>
          </div>
        </div>
      )}

      <div className="sm:col-span-2 flex items-center gap-2 pt-1">
        <Checkbox id="walkin-send-welcome" checked={form.send_welcome_whatsapp} data-testid="walkin-send-welcome"
          onCheckedChange={(v) => setForm((f) => ({ ...f, send_welcome_whatsapp: !!v }))} />
        <Label htmlFor="walkin-send-welcome" className="font-normal cursor-pointer">
          Send instant Student Portal magic link via WhatsApp
        </Label>
      </div>

      <div className="sm:col-span-2">
        <Button data-testid="walkin-submit-button" type="submit" disabled={busy} className="w-full bg-brand">
          {busy ? "Saving…" : "Capture Lead"}
        </Button>
      </div>
    </form>
  );
}

function DuplicateLeadNotice({ dup, checking, onOpen }) {
  if (checking && !dup) {
    return <p className="text-xs text-slate-400">Checking for an existing lead…</p>;
  }
  if (!dup) return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
      data-testid="walkin-duplicate-notice">
      <p>
        ⚠️ Existing lead found for <b>{dup.name}</b> (Stage: {dup.stage}, Counselor: {dup.counselor}).
      </p>
      {onOpen && (
        <button type="button" onClick={() => onOpen(dup.leadId)} data-testid="walkin-duplicate-open"
          className="mt-1 font-medium underline hover:text-amber-900">
          Open that lead's record
        </button>
      )}
    </div>
  );
}

const SAMPLE_CSV =
  "full_name,phone,email,course,notes\n" +
  "Aarav Sharma,9876543210,aarav.sharma@example.com,MBA,Met at the Delhi education fair\n" +
  "Diya Patel,08123456789,diya.patel@example.com,B.Tech,Interested in the CS specialisation\n" +
  "Kabir Nair,+91 99887 76655,,BBA,Phone-only lead from a vendor list\n";

const SOURCE_SUGGESTIONS = [
  "External Database", "Vendor List", "Direct Outreach", "Competitive Dump",
  "Education Fair", "Agency Vendor", "Alumni Referral",
];

function ImportForm({ onDone }) {
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("External Database");
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | uploading | processing
  const [pct, setPct] = useState(0);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const pick = (f) => {
    setErr("");
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      setErr("Choose a .csv or .xlsx file.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setErr("That file is larger than 5 MB.");
      return;
    }
    setFile(f);
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "edupulse_leads_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file || phase !== "idle") return;
    setErr("");
    setPct(0);
    setPhase("uploading");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("default_source", source.trim() || "External Database");
    try {
      const r = await api.post("/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (e) => {
          if (e.total) {
            const p = Math.round((e.loaded / e.total) * 100);
            setPct(p);
            if (p >= 100) setPhase("processing");
          }
        },
      });
      const s = r.data;
      setResult(s);
      toast.success(
        `${s.inserted} imported · ${s.duplicates} duplicate${s.duplicates === 1 ? "" : "s"} skipped` +
        (s.errors ? ` · ${s.errors} error${s.errors === 1 ? "" : "s"}` : "")
      );
    } catch (e) {
      setPhase("idle");
      setErr(formatApiError(e.response?.data?.detail) || "Import failed.");
    }
  };

  if (result) {
    return (
      <div className="space-y-4" data-testid="import-summary">
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Rows read", result.total, "text-slate-800"],
            ["Imported", result.inserted, "text-emerald-700"],
            ["Duplicates skipped", result.duplicates, "text-amber-700"],
            ["Errors", result.errors, result.errors ? "text-red-700" : "text-slate-800"],
          ].map(([k, v, cls]) => (
            <div key={k} className="rounded-lg border border-slate-200 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{k}</div>
              <div className={`mt-1 text-2xl font-bold ${cls}`}>{v}</div>
            </div>
          ))}
        </div>
        {result.truncated && (
          <p className="text-xs text-amber-700">Only the first 5,000 rows were processed.</p>
        )}
        {result.assigned_counselors > 0 && (
          <p className="text-xs text-slate-500">
            Round-robin assigned across {result.assigned_counselors} counsellor
            {result.assigned_counselors === 1 ? "" : "s"} · source “{result.source}”.
          </p>
        )}
        {result.error_rows?.length > 0 && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 max-h-32 overflow-auto">
            {result.error_rows.map((e, i) => (
              <div key={i} className="text-xs text-slate-600">Row {e.row}: {e.reason}</div>
            ))}
          </div>
        )}
        <Button className="w-full bg-brand" data-testid="import-done" onClick={onDone}>Done</Button>
      </div>
    );
  }

  const busy = phase !== "idle";

  return (
    <div className="space-y-4" data-testid="import-form">
      <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden"
        onChange={(e) => pick(e.target.files?.[0])} />

      <div
        role="button"
        tabIndex={0}
        onClick={() => !busy && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
        data-testid="import-dropzone"
        className={`rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
          dragging ? "border-brand bg-slate-50" : "border-slate-300 hover:border-slate-400"
        } ${busy ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
      >
        <Upload className="w-7 h-7 mx-auto text-slate-400" />
        {file ? (
          <p className="mt-2 text-sm font-medium text-slate-800">{file.name}</p>
        ) : (
          <>
            <p className="mt-2 text-sm font-medium text-slate-700">Drop a CSV / XLSX here, or click to browse</p>
            <p className="text-xs text-slate-400 mt-0.5">Up to 5 MB · 5,000 rows</p>
          </>
        )}
      </div>

      <button type="button" onClick={downloadTemplate} data-testid="import-template"
        className="text-xs font-medium text-brand hover:underline inline-flex items-center gap-1">
        <Download className="w-3.5 h-3.5" /> Download sample CSV template
      </button>

      <div>
        <Label htmlFor="import-source">Lead source</Label>
        <Input id="import-source" list="import-source-suggestions" value={source}
          onChange={(e) => setSource(e.target.value)} className="mt-1.5" data-testid="import-source"
          placeholder="e.g. Vendor List" />
        <datalist id="import-source-suggestions">
          {SOURCE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>

      {busy && (
        <div className="space-y-1.5" data-testid="import-progress">
          <Progress value={phase === "processing" ? 100 : pct} />
          <p className="text-xs text-slate-500">
            {phase === "processing"
              ? "Deduplicating and assigning counsellors…"
              : `Uploading… ${pct}%`}
          </p>
        </div>
      )}
      {err && <p className="text-sm text-red-600" data-testid="import-error">{err}</p>}

      <Button className="w-full bg-brand" disabled={!file || busy} onClick={submit} data-testid="import-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import Leads"}
      </Button>
    </div>
  );
}
